/**
 * Device Fingerprinting Utility
 *
 * Generates unique fingerprints for device identification and provides
 * device information for the device authentication flow.
 */

export interface DeviceInfo {
  fingerprint: string;
  deviceName: string;
  browser: string;
  os: string;
}

const DEVICE_TOKEN_KEY = 'elevair_device_token';

/**
 * Generate a unique fingerprint for the current device
 */
export async function generateFingerprint(): Promise<string> {
  const components = [
    navigator.userAgent,
    navigator.language,
    `${screen.width}x${screen.height}`,
    screen.colorDepth.toString(),
    new Date().getTimezoneOffset().toString(),
    navigator.hardwareConcurrency?.toString() || 'unknown',
    await getCanvasFingerprint(),
    await getWebGLFingerprint(),
  ];

  return hashString(components.join('|'));
}

/**
 * Get full device info for display and registration
 */
export function getDeviceInfo(): Omit<DeviceInfo, 'fingerprint'> {
  const ua = navigator.userAgent;

  return {
    deviceName: getDeviceName(ua),
    browser: getBrowser(ua),
    os: getOS(ua),
  };
}

/**
 * Get device token from localStorage
 */
export function getDeviceToken(): string | null {
  return localStorage.getItem(DEVICE_TOKEN_KEY);
}

/**
 * Set device token in localStorage
 */
export function setDeviceToken(token: string): void {
  localStorage.setItem(DEVICE_TOKEN_KEY, token);
}

/**
 * Clear device token from localStorage
 */
export function clearDeviceToken(): void {
  localStorage.removeItem(DEVICE_TOKEN_KEY);
}

// ============ Helper Functions ============

async function getCanvasFingerprint(): Promise<string> {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-canvas';

    canvas.width = 200;
    canvas.height = 50;

    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Elevair Auth', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Fingerprint', 4, 30);

    return canvas.toDataURL().slice(-50);
  } catch {
    return 'canvas-error';
  }
}

async function getWebGLFingerprint(): Promise<string> {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return 'no-webgl';

    const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return 'no-debug-info';

    const vendor = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const renderer = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

    return `${vendor}~${renderer}`;
  } catch {
    return 'webgl-error';
  }
}

async function hashString(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

function getDeviceName(ua: string): string {
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android.*Mobile/.test(ua)) return 'Android Phone';
  if (/Android/.test(ua)) return 'Android Tablet';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Linux/.test(ua)) return 'Linux PC';
  return 'Unknown Device';
}

function getBrowser(ua: string): string {
  if (/Edg\//.test(ua)) return 'Edge ' + (ua.match(/Edg\/(\d+)/) || [])[1];
  if (/Chrome\//.test(ua)) return 'Chrome ' + (ua.match(/Chrome\/(\d+)/) || [])[1];
  if (/Safari\//.test(ua) && !/Chrome/.test(ua))
    return 'Safari ' + (ua.match(/Version\/(\d+)/) || [])[1];
  if (/Firefox\//.test(ua)) return 'Firefox ' + (ua.match(/Firefox\/(\d+)/) || [])[1];
  return 'Unknown Browser';
}

function getOS(ua: string): string {
  if (/iPhone OS (\d+)/.test(ua)) return 'iOS ' + (ua.match(/iPhone OS (\d+)/) || [])[1];
  if (/iPad.*OS (\d+)/.test(ua)) return 'iPadOS ' + (ua.match(/OS (\d+)/) || [])[1];
  if (/Android (\d+)/.test(ua)) return 'Android ' + (ua.match(/Android (\d+)/) || [])[1];
  if (/Mac OS X (\d+[._]\d+)/.test(ua))
    return 'macOS ' + (ua.match(/Mac OS X (\d+[._]\d+)/) || [])[1]?.replace('_', '.');
  if (/Windows NT 10/.test(ua)) return 'Windows 10/11';
  if (/Windows NT/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown OS';
}
